import { useMutation, useQuery, useQueryClient } from "react-query";
import { getInventory, transferStock, getTransferHistory, reserveStock, exportInventoryPdf, exportTransferHistoryPdf } from "../../../services";
import type { TransferPayload, ReservePayload } from "../../../privatePages/Inventory/components/ProductsTable/types";
import { QUERY_KEYS } from "../queryKeys";
import { errorToast } from "../../../services/toasts";

/**
 * Fetches the full inventory for the authenticated supplier.
 * Returns products with per-location stock and the list of storage addresses.
 * GET /v2/inventory
 */
export const useGetInventory = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_INVENTORY],
    queryFn: getInventory,
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation to atomically transfer stock between two storage addresses.
 * POST /v2/inventory/transfer
 */
export const useTransferStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TransferPayload) => transferStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to reserve stock for a client or quote.
 * POST /v2/inventory/reserve
 */
export const useReserveStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReservePayload) => reserveStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Fetches the transfer history for the authenticated supplier.
 * Optionally scoped to a single product with pagination.
 * GET /v2/inventory/transfer-history
 */
export const useGetTransferHistory = (productId?: number, page?: number, limit?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_TRANSFER_HISTORY, productId ?? null, page ?? null, limit ?? null],
    queryFn: () => getTransferHistory(productId, page, limit),
    refetchOnWindowFocus: false,
    enabled: true,
  });
};

/**
 * Mutation to export inventory as PDF.
 * On success, opens the generated PDF URL in a new tab.
 * GET /v2/inventory/export
 */
export const useExportInventoryPdf = () => {
  return useMutation({
    mutationFn: () => exportInventoryPdf(),
    onSuccess: (data) => {
      window.open(data.url, "_blank");
    },
    onError: () => {
      errorToast("Error al exportar inventario");
    },
  });
};

/**
 * Mutation to export transfer history as PDF.
 * On success, opens the generated PDF URL in a new tab.
 * GET /v2/inventory/transfer-history/export
 */
export const useExportTransferHistoryPdf = () => {
  return useMutation({
    mutationFn: () => exportTransferHistoryPdf(),
    onSuccess: (data) => {
      window.open(data.url, "_blank");
    },
    onError: () => {
      errorToast("Error al exportar historial de traslados");
    },
  });
};
